import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import PageHeader from "../../components/layout/PageHeader";
import Table from "../../components/ui/Table";
import Badge from "../../components/ui/Badge";
import Spinner from "../../components/ui/Spinner";
import EmptyState from "../../components/ui/EmptyState";
import RequestActions, {
  CandidateCell,
  ReviewedCell,
  fmtDate,
  selectColumn,
} from "../../components/requests/RequestActions";
import RequestFilters, {
  StatusBadge,
  useRequestFilters,
} from "../../components/requests/RequestFilters";
import {
  BulkActionBar,
  useRowSelection,
} from "../../components/requests/BulkSelection";
import ExcelDownloadModal from "../../components/requests/ExcelDownloadModal";
import Button from "../../components/ui/Button";
import { exportCandidateRequests } from "../../api/excelTemplates";
import { RejectRemarksModal } from "./BulkRequestApprovals";
import {
  listPassportRequests,
  approvePassportRequest,
  rejectPassportRequest,
} from "../../api/passportRegister";
import {
  requestsQueryKey,
  requestsCountQueryKey,
} from "../../hooks/useCandidateRequestCount";

const dash = (v) => v || <span className="text-gray-400">—</span>;

const RejectModal = (props) => (
  <RejectRemarksModal {...props} title="Reject passport request" />
);

const searchFields = (row) => [
  row.candidate.full_name,
  row.candidate.reference_no,
  row.candidate.passport_no,
  row.register_no,
  row.reason,
  row.requested_by,
];
const requestedAt = (row) => row.requested_at;

export default function PassportApprovals() {
  const queryClient = useQueryClient();
  const { filters, set, apply, reset, dirty, candidate } = useRequestFilters(
    searchFields,
    requestedAt,
  );
  const query = useQuery({
    queryKey: [
      ...requestsQueryKey("passport"),
      filters.status,
      candidate.params,
    ],
    queryFn: () =>
      listPassportRequests({ ...candidate.params, status: filters.status }),
  });
  const allRows = query.data || [];
  const rows = apply(allRows);
  // Pending rows can be approved/rejected; approved ones can be downloaded as Excel.
  const selection = useRowSelection(
    rows,
    (r) => r.id,
    (r) => r.status === "pending" || r.status === "approved",
  );
  const pendingIds = new Set(
    rows.filter((r) => r.status === "pending").map((r) => r.id),
  );
  const approvedIds = new Set(
    rows.filter((r) => r.status === "approved").map((r) => r.id),
  );
  const downloadIds = selection.selectedIds.filter((id) => approvedIds.has(id));
  const [downloadOpen, setDownloadOpen] = useState(false);

  const refetch = () => {
    queryClient.invalidateQueries({ queryKey: requestsQueryKey("passport") });
    queryClient.invalidateQueries({
      queryKey: requestsCountQueryKey("passport"),
    });
    queryClient.invalidateQueries({
      queryKey: ["passport-request-candidates"],
    });
    queryClient.invalidateQueries({ queryKey: ["passport-history"] });
  };

  const columns = [
    selectColumn(
      selection,
      (r) => r.id,
      (r) => r.status === "pending" || r.status === "approved",
    ),
    {
      key: "candidate",
      label: "Candidate",
      render: (row) => <CandidateCell candidate={row.candidate} />,
    },
    {
      key: "register_no",
      label: "Register No",
      render: (row) => dash(row.register_no),
    },
    {
      key: "passport_no",
      label: "Passport No",
      render: (row) => dash(row.candidate.passport_no),
    },
    {
      key: "action",
      label: "Action",
      render: (row) => (
        <Badge tone={row.action === "collect" ? "amber" : "purple"}>
          {row.action === "collect" ? "Collect" : "Give"}
        </Badge>
      ),
    },
    {
      key: "reason",
      label: "Reason",
      render: (row) => (
        <div>
          {row.reason}
          {row.note && (
            <div className="text-xs italic text-gray-500">"{row.note}"</div>
          )}
        </div>
      ),
    },
    {
      key: "requested",
      label: "Requested by",
      render: (row) => (
        <div>
          {row.requested_by}
          <div className="text-xs text-gray-400">
            {fmtDate(row.requested_at)}
          </div>
        </div>
      ),
    },
    {
      key: "status",
      label: "Status",
      render: (row) => (
        <div>
          <StatusBadge status={row.status} />
          <ReviewedCell row={row} />
        </div>
      ),
    },
    {
      key: "actions",
      label: "",
      render: (row) => (
        <RequestActions
          pending={row.status === "pending"}
          onApprove={() => approvePassportRequest(row.id)}
          onReject={(reason) => rejectPassportRequest(row.id, reason)}
          onResolved={refetch}
          approvedMessage="Passport request approved."
          rejectedMessage="Passport request rejected."
          RejectModal={RejectModal}
        />
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Passport Request"
        description="Passport collect and give requests — approve or reject them here."
      />

      <RequestFilters
        candidate={candidate}
        filters={filters}
        set={set}
        reset={reset}
        dirty={dirty}
        total={allRows.length}
        shown={rows.length}
      />

      <BulkActionBar
        actionableIds={pendingIds}
        selectAllLabel={`Select all (${selection.selectableCount})`}
        extraActions={
          downloadIds.length > 0 && (
            <Button variant="secondary" onClick={() => setDownloadOpen(true)}>
              Download Excel ({downloadIds.length})
            </Button>
          )
        }
        selection={selection}
        onApprove={(id) => approvePassportRequest(id)}
        onReject={(id, reason) => rejectPassportRequest(id, reason)}
        onDone={refetch}
      />

      {downloadOpen && (
        <ExcelDownloadModal
          open
          onClose={() => setDownloadOpen(false)}
          ids={downloadIds}
          onExport={(ids, templateId) =>
            exportCandidateRequests("passport", ids, templateId)
          }
        />
      )}

      {query.isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          title={dirty ? "No matching requests" : "Nothing pending"}
          message={
            dirty
              ? "No requests match the current filters."
              : "No passport requests are currently awaiting review."
          }
        />
      ) : (
        <Table columns={columns} data={rows} />
      )}
    </div>
  );
}
